import axios from 'axios';
import {readStoredSid} from '@api/auth';
import {IS_MOCK} from '@api/mock-mode';
import {adaptAnalyticsInsight} from './adapter';
import {
  createMockAnalyticsInsightTask,
  getMockAnalyticsInsight,
  listMockAnalyticsInsights,
  updateMockAnalyticsInsightMyState,
} from './mock';
import type {
  AnalyticsInsightCard,
  AnalyticsInsightCardDTO,
  AnalyticsInsightInclude,
  AnalyticsInsightMyState,
  AnalyticsInsightMyStatePatch,
  AnalyticsInsightsPage,
  AnalyticsInsightsPageDTO,
  AnalyticsInsightsRequest,
  CreateAnalyticsInsightTaskInput,
  CreatedAnalyticsInsightTask,
} from './types';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

const delay = (ms = 200) => new Promise((resolve) => setTimeout(resolve, ms));

/** API сохранённых аналитических инсайтов workspace. */
export const analyticsInsightsClient = {
  /** Получить страницу готовых инсайтов. Cursor передаётся без разбора. */
  async list(
    workspaceSid: string,
    request: AnalyticsInsightsRequest,
  ): Promise<AnalyticsInsightsPage> {
    if (IS_MOCK) {
      await delay();
      return listMockAnalyticsInsights(request, {
        workspaceSid,
        actorSid: readStoredSid() ?? 'mock-user',
      });
    }
    const {data} = await http.post<AnalyticsInsightsPageDTO>(
      `/workspaces/${workspaceSid}/analytics-insights`,
      request,
    );
    return {...data, rows: data.rows.map(adaptAnalyticsInsight)};
  },

  /** Получить детальную карточку с выбранными безопасными проекциями. */
  async get(
    workspaceSid: string,
    insightSid: string,
    include: AnalyticsInsightInclude[],
  ): Promise<AnalyticsInsightCard> {
    if (IS_MOCK) {
      await delay(120);
      return getMockAnalyticsInsight(insightSid, include, {
        workspaceSid,
        actorSid: readStoredSid() ?? 'mock-user',
      });
    }
    const params = new URLSearchParams();
    include.forEach((value) => params.append('include', value));
    const {data} = await http.get<AnalyticsInsightCardDTO>(
      `/workspaces/${workspaceSid}/analytics-insights/${insightSid}`,
      {params},
    );
    return adaptAnalyticsInsight(data);
  },

  /** Сохранить абсолютное собственное состояние с проверкой revision. */
  updateMyState: async (
    workspaceSid: string,
    insightSid: string,
    dto: AnalyticsInsightMyStatePatch,
  ): Promise<AnalyticsInsightMyState> => {
    if (IS_MOCK) {
      await delay(120);
      return updateMockAnalyticsInsightMyState(insightSid, dto, {
        workspaceSid,
        actorSid: readStoredSid() ?? 'mock-user',
      });
    }
    const {data} = await http.patch<AnalyticsInsightMyState>(
      `/workspaces/${workspaceSid}/analytics-insights/${insightSid}/my-state`,
      dto,
    );
    return data;
  },

  /** Создать пользовательскую задачу из инсайта с idempotency key команды. */
  async createTask(
    workspaceSid: string,
    insightSid: string,
    input: CreateAnalyticsInsightTaskInput,
    idempotencyKey: string,
  ): Promise<CreatedAnalyticsInsightTask> {
    if (IS_MOCK) {
      await delay(180);
      return createMockAnalyticsInsightTask(insightSid, input, idempotencyKey);
    }
    const {data} = await http.post<CreatedAnalyticsInsightTask>(
      `/workspaces/${workspaceSid}/analytics-insights/${insightSid}/tasks`,
      input,
      {headers: {'Idempotency-Key': idempotencyKey}},
    );
    return data;
  },
};
